<template>
    <p
        class="text-ink flex-grow font-light font-oceanside"
        :class="[isPrintMode ? 'text-lg' : 'text-sm leading-tight']"
    >
        <!-- Empty selections -->
        <template v-if="selections.find((x) => x.name === 'NO_TOE_ENGRAVING')">
            <div class="px-3 py-3 flex justify-between">
                <div class="w-1/4 flex flex-col justify-center font-semibold">
                    {{ selections[0].category }}
                </div>
                <div>
                    {{ $t('NO_TOE_ENGRAVING') }}
                </div>
                <div class="w-1/4 flex"></div>
            </div>
        </template>
        <template v-else-if="selections.find((x) => x.name === 'NO_STAMPING')">
            <div class="px-3 py-3 flex justify-between">
                <div class="w-1/4 flex flex-col justify-center font-semibold">
                    {{ selections[0].category }}
                </div>
                <div>
                    {{ $t('NO_STAMPING') }}
                </div>
                <div class="w-1/4 flex"></div>
            </div>
        </template>

        <!-- Filled selections -->
        <template v-else>
            <template v-if="showByCategory">
                <div
                    v-for="(category, index) of selectionsByCategory" :key="category.category"
                    class="px-3 py-3 flex justify-between"
                    :class="[
                        index % 2 === 0 ? (
                            isPrintMode ? 'bg-gray-light' : 'bg-gray-FAFAFA'
                        ) : 'bg-white',
                        {
                            'border': category.values.find((x) => x.name === 'MAKE_SELECTION'),
                            'justify-start': category.values.find((x) => x.name === 'MAKE_SELECTION'),
                        }
                    ]"
                >
                    <div
                        class="flex flex-col justify-center font-semibold"
                        :class="[isPrintMode ? 'w-[100px] shrink-0' : 'w-[64px]']"
                    >
                        {{ getCategoryFromPlacementCode(category.category) }}
                    </div>

                    <div
                        class="flex flex-col flex-1 justify-start ml-4"
                        v-if="category.values.find((x) => x.name === 'MAKE_SELECTION')"
                    >
                        <a

                            class="inline-block underline text-red cursor-pointer"
                            @click="redirectToPanel"
                            :class="[isPrintMode ? 'text-lg' : 'text-[0.85rem]']"
                        >
                            {{ $t('MAKE_SELECTIONS') }}
                        </a>
                    </div>

                    <div
                        v-else
                        class="flex flex-col self-end ml-4"
                        :class="[isPrintMode ? 'flex-1' : 'w-1/2']"
                    >
                        <paintfill-summary-link
                            v-if="!isPrintMode && shouldShowPaintfillSummaryLink(category.values)"
                        />
                        <current-selection-detailed-description
                            v-else
                            :description="category.values"
                        />
                    </div>

                    <div class="w-1/4 flex flex-col justify-center items-end">
                        {{ price || '─ ─' }}
                    </div>
                </div>
            </template>
            <div
                v-else
                class="px-3 py-3 flex justify-between"
                :class="[
                    isPrintMode ? 'bg-gray-light' : 'bg-gray-FAFAFA',
                    {
                        'border': selections.find((x) => x.name === 'MAKE_SELECTION'),
                    }]
                "
            >
                <div
                    class="flex flex-col justify-center font-semibold text-nowrap"
                    :class="[isPrintMode ? 'w-[100px] shrink-0' : 'w-[64px]']"
                >
                    {{ formattedCategoryName }}
                </div>
                <div
                    class="flex flex-col justify-center ml-4"
                    :class="{ 'flex-1': isPrintMode || placementData.code === 'Paintfill' }"
                    v-if="selections.find((x) => x.name === 'MAKE_SELECTION')"
                >
                    <a

                        class="inline-block underline text-red cursor-pointer"
                        @click="redirectToPanel"
                        :class="[isPrintMode ? 'text-lg' : '0.85rem']"
                    >
                        {{ $t('MAKE_SELECTIONS') }}
                    </a>
                </div>

                <div
                    v-else
                    class="flex flex-col self-center ml-4"
                    :class="[isPrintMode ? 'flex-1' : 'w-1/2']"
                >
                    <paintfill-summary-link
                        class="text-[14px]"
                        v-if="!isPrintMode && shouldShowPaintfillSummaryLink(selections)"
                    />
                    <current-selection-detailed-description
                        v-else
                        :description="selections"
                    />
                </div>

                <!-- <div class="flex-1" /> -->

                <div class="w-1/4 flex flex-col justify-center items-end">
                    {{ price || '─ ─' }}
                </div>
            </div>
        </template>
    </p>
</template>

<script>
    import allStores from '../stores/all';
    import CurrentSelectionDetailedDescription from './CurrentSelectionDetailedDescription.vue';
    import PaintfillSummaryLink from './PaintfillSummaryLink.vue';

    const PAINTFILL_SUMMARY_PRICE_GROUP_CODES = ['Paintfill', 'BackPaintfill', 'TopPaintfill'];
    const PAINTFILL_SUMMARY_LINK = 'PAINTFILL_SUMMARY_LINK';

    export default {
        mixins: [allStores],

        components: {
            CurrentSelectionDetailedDescription,
            PaintfillSummaryLink,
        },

        props: {
            placementData: {
                type: Object,
                required: true,
            },
        },

        computed: {
            isPrintMode() {
                return this.appStore.isPrintMode;
            },

            shouldShowSummary() {
                return this.appStore.shouldShowSummary;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            selectedProducts() {
                return this.customizerStore.selectedProducts;
            },

            currentComponent() {
                const placement = this.placementData.placements[0];

                if (placement && placement.code !== 'Wedge') {
                    return this.viewController.selectedAtPlacement(placement.code);
                }

                return null;
            },

            isPaintfillSummaryPriceGroup() {
                return PAINTFILL_SUMMARY_PRICE_GROUP_CODES.includes(this.placementData?.code);
            },

            paintfillSummaryPriceOwnerCode() {
                return PAINTFILL_SUMMARY_PRICE_GROUP_CODES.find((groupCode) => {
                    const component = this.selectedComponentForSummaryGroup(groupCode);
                    const price = this.rawPriceForComponent(component);

                    return price != null && Number(price) !== 0;
                });
            },

            shouldShowPrice() {
                if (!this.isPaintfillSummaryPriceGroup) {
                    return true;
                }

                return this.placementData?.code === this.paintfillSummaryPriceOwnerCode;
            },

            shouldShowPaintfillLocationLabels() {
                return ['Paintfill', 'TopPaintfill', 'BackPaintfill'].includes(this.placementData?.code);
            },

            placements() {
                const visible = [];

                if (!this.placementData?.placements) {
                    return [];
                }

                this.placementData.placements.forEach((placement) => {
                    if (placement.custom?.['v-show-selected'] === 'no') {
                        return;
                    }

                    visible.push(placement);
                });

                return visible;
            },

            noCustomizationSelection() {
                if (
                    (this.placementData.name === 'Top' || this.placementData.code === 'TopPaintfill')
                    && this.customizerStore.isTopNoCustomizationSelected()
                ) {
                    return this.$t('NO_CUSTOMIZATION');
                }

                if (
                    (this.placementData.name === 'Back' || this.placementData.code === 'BackPaintfill')
                    && this.customizerStore.isBackNoCustomizationSelected()
                ) {
                    return this.$t('NO_CUSTOMIZATION');
                }

                return null;
            },

            selections() {
                // Handle unified Front group (Character Stamping + Stamps) as combined, category-based selections
                if (this.placementData && (this.placementData.name === 'Face/Sole' || this.placementData.code === 'Face/Sole')) {
                    const subgroupCodes = this.placementData.subgroups?.map((group) => group.code)
                        || ['CharacterStamping', 'Stamps'];

                    return subgroupCodes.flatMap((groupCode) => this.buildSelectionsForGroup(groupCode));
                }

                if (!this.isPrintMode && this.placementData?.code === 'Paintfill') {
                    const category = this.getCategoryFromPlacementCode('Paintfill');

                    if (!this.customizerStore.isPaintfillComplete('Face/Sole')) {
                        return [{
                            name: 'MAKE_SELECTION',
                            placementCode: 'PutterFrontPaintfill',
                            category,
                        }];
                    }

                    return [{
                        name: PAINTFILL_SUMMARY_LINK,
                        placementCode: 'PutterFrontPaintfill',
                        category,
                    }];
                }

                const names = [];
                const category = this.getCategoryFromPlacementCode(this.placementData.code);

                if (this.noCustomizationSelection) {
                    return [{
                        name: this.noCustomizationSelection,
                        placementCode: this.placements[0]?.code || this.placementData.code,
                        category,
                    }];
                }

                // Potential cases:
                //
                // First placement is important and toggles other placements.
                // Multiple "equal" placements.

                let hasMain = false;

                // If the first placement is "important" (toggles other placements in a group) and a placeholder, short circuit the evaluation.
                const isSpecificationsGroup = this.placementData?.code === 'Specifications';

                if (this.placements.length > 0) {
                    const firstPlacement = this.placements[0];

                    if (firstPlacement.custom?.['v-main-selection'] === 'yes') {
                        const firstSelection = this.viewController?.selectedAtPlacement(firstPlacement.code);

                        hasMain = true;

                        if (firstSelection?.placeholder) {
                            names.push({
                                name: 'MAKE_SELECTION',
                                placementCode: firstPlacement.code,
                                category,
                            });
                        }
                    }
                }

                let index = 0;

                // eslint-disable-next-line no-restricted-syntax
                for (const placement of this.placements) {
                    // Special case for wedges.
                    if (placement.code !== 'Putter') {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const finishes = new Set();
                    const orientations = new Set();
                    const lengths = new Set();

                    // eslint-disable-next-line no-restricted-syntax
                    for (const p of this.selectedProducts) {
                        const finish = p.custom?.['t-finish-name'];
                        if (finish) {
                            finishes.add(finish);
                        }

                        const orientation = p.custom?.['t-orientation-name'];
                        if (orientation) {
                            orientations.add(orientation);
                        }

                        const length = p.custom?.['t-setcomp-name'];
                        if (length) {
                            lengths.add(length);
                        }
                    }

                    if (finishes.size === 0) {
                        // Find a currently selected finish, even if there is no selected club.
                        if (this.viewController.blueprint.custom['t-finish-name']) {
                            finishes.add(this.viewController.blueprint.custom['t-finish-name']);
                        }
                    }

                    if (finishes.size > 0) {
                        // eslint-disable-next-line no-restricted-syntax
                        for (const f of finishes) {
                            names.push({
                                name: f,
                                placementCode: placement.code,
                                category: this.$t('CATEGORY_FINISH'),
                            });
                        }
                    }

                    if (orientations.size > 0) {
                        // eslint-disable-next-line no-restricted-syntax
                        for (const o of orientations) {
                            names.push({
                                name: o,
                                placementCode: placement.code,
                                category: this.$t('CATEGORY_HAND'),
                            });
                        }
                    } else {
                        names.push({
                            name: 'MAKE_SELECTION',
                            placementCode: placement.code,
                            category: this.$t('CATEGORY_HAND'),
                        });
                    }

                    if (lengths.size === 0) {
                        // Show 'make selections' for wedges.
                        names.push({
                            name: 'MAKE_SELECTION',
                            placementCode: placement.code,
                            category: this.$t('LENGTH'),
                        });
                    } else if (lengths.size > 0) {
                        // eslint-disable-next-line no-restricted-syntax
                        for (const o of lengths) {
                            names.push({
                                name: o,
                                placementCode: placement.code,
                                category: this.$t('LENGTH'),
                            });
                        }
                    }
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const placement of this.placements) {
                    if (isSpecificationsGroup && ['PutterParent', 'Putter'].includes(placement.code)) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    // Skip initials colors.
                    if (placement.code === 'StampingColorsLine1' || placement.code === 'StampingColorsLine2' || placement.code === 'StampingColorsSymbols') {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const placementCategory = isSpecificationsGroup
                        ? this.getSpecificationCategoryFromPlacement(placement)
                        : category;
                    const selectedAtPlacement = this.viewController?.selectedAtPlacement(placement.code);
                    const selection = selectedAtPlacement?.placeholder ? null : selectedAtPlacement;

                    if (selectedAtPlacement?.code === 'Stamp-Stamp-NoThanks') {
                        names.push({
                            name: 'NO_TOE_ENGRAVING',
                            placementCode: placement.code,
                            category: placementCategory,
                        });
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    if (selectedAtPlacement?.code === 'FlightLine-FlightLine-NoThanks') {
                        names.push({
                            name: this.$t('NO_FLIGHT_LINES'),
                            placementCode: placement.code,
                            category: placementCategory,
                        });
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    if (selectedAtPlacement?.code === 'StampingStyle-StampingStyle-1') {
                        names.push({
                            name: 'NO_STAMPING',
                            placementCode: placement.code,
                            category: placementCategory,
                        });
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    if (selection) {
                        if (placement.code === 'Shaft') {
                            const manufacturer = selectedAtPlacement.custom?.['v-manufacturer'];
                            const name = selectedAtPlacement.custom?.['v-name'];

                            names.push({
                                name: manufacturer,
                                category: this.$t('CATEGORY_SHAFT'),
                            });
                            names.push({
                                name: `${name}`,
                                category: this.$t('CATEGORY_SHAFT'),
                            });
                        } else if (placement.code === 'Grip') {
                            // Special case for Grips...

                            const manufacturer = selectedAtPlacement.custom?.['v-manufacturer'];
                            const name = selectedAtPlacement.custom?.['v-name'];

                            names.push({
                                name: manufacturer,
                                category: this.$t('CATEGORY_GRIP'),
                            });
                            names.push({
                                name: `${name}`,
                                category: this.$t('CATEGORY_GRIP'),
                            });
                        } else {
                            const name = this.shouldShowPaintfillLocationLabels
                                ? this.getPaintfillLocationSelectionName(placement, selection)
                                : this.getDescriptionText(selection.description);

                            names.push({
                                name,
                                placementCode: placement.code,
                                category: placementCategory,
                            });
                        }
                    } else if (hasMain) {
                        names.push({
                            name: index === 0 ? 'MAKE_SELECTION' : '',
                            placementCode: placement.code,
                            category: placementCategory,
                        });
                    } else {
                        names.push({
                            name: 'MAKE_SELECTION',
                            placementCode: placement.code,
                            category: placementCategory,
                        });
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const characters = [];
                    const characterColors = [];

                    // The first placement may also contain custom text.
                    if (selection?.custom?.['v-stamping-text-1']) {
                        if (selection.custom['v-stamping-text-1'].length > 0) {
                            characters.push(selection.custom['v-stamping-text-1']);

                            const color = this.placements.find((x) => x.code === 'StampingColorsLine1');
                            if (color) {
                                const colorSelection = this.viewController.selectedAtPlacement(color.code);
                                if (colorSelection && !colorSelection.placeholder) {
                                    characterColors.push(this.getDescriptionText(colorSelection.description));
                                }
                            }
                        }
                    }

                    if (selection?.custom?.['v-stamping-text-2']) {
                        if (selection.custom['v-stamping-text-2'].length > 0) {
                            characters.push(selection.custom['v-stamping-text-2']);

                            const color = this.placements.find((x) => x.code === 'StampingColorsLine2');
                            if (color) {
                                const colorSelection = this.viewController.selectedAtPlacement(color.code);
                                if (colorSelection && !colorSelection.placeholder) {
                                    characterColors.push(this.getDescriptionText(colorSelection.description));
                                }
                            }
                        }
                    }

                    if (characters.length > 0) {
                        names.push({
                            name: characters.join(' / '),
                            placementCode: placement.code,
                            category: placementCategory,
                        });

                        names.push({
                            name: characterColors.join(' / '),
                            placementCode: 'StampingColorsLines',
                            category: placementCategory,
                        });
                    }

                    // Laser etching custom text
                    if (selection?.custom?.['v-etching-text-1']) {
                        names.push({
                            name: selection.custom['v-etching-text-1'],
                            placementCode: placement.code,
                            category: placementCategory,
                        });
                    }

                    // If stamping... find stamping color.
                    if (placement.code === 'StampSymbols' && selection && !selection.placeholder) {
                        const color = this.placements.find((x) => x.code === 'StampingColorsSymbols');
                        if (color) {
                            const colorSelection = this.viewController.selectedAtPlacement(color.code);
                            if (colorSelection && !colorSelection.placeholder) {
                                names.push({
                                    name: this.getDescriptionText(colorSelection.description),
                                    placementCode: placement.code,
                                    category: placementCategory,
                                });
                            }
                        }
                    }

                    index += 1;
                }

                const notEmpty = names.filter((x) => x.name.length > 0);

                const deDupped = [];

                notEmpty.forEach((entry) => {
                    if (!deDupped.find((x) => x.name === entry.name && x.category === entry.category && x.placementCode === entry.placementCode)) {
                        deDupped.push(entry);
                    }
                });

                return deDupped;
            },

            selectionsByCategory() {
                const categories = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const s of this.selections) {
                    let category = categories.find((x) => x.category === s.category);

                    if (category == null) {
                        category = {
                            category: s.category,
                            values: [],
                        };

                        categories.push(category);
                    }

                    category.values.push(s);
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const c of categories) {
                    // If a category contains 'make selections' choice, ignore all other values for that category.
                    if (c.values.some((x) => x.name === 'MAKE_SELECTION')) {
                        c.values = c.values.filter((x) => x.name === 'MAKE_SELECTION');
                    }
                }

                return categories;
            },

            showByCategory() {
                if (this.placementData && (
                    this.placementData.code === 'Specifications'
                    || this.placementData.code === 'CharacterStamping'
                    || this.placementData.code === 'Stamps'
                    || this.placementData.name === 'Face/Sole'
                    || this.placementData.code === 'Face/Sole'
                    || this.placementData.name === 'BackPaintfill'
                    || this.placementData.code === 'BackPaintfill'
                    || this.placementData.name === 'TopPaintfill'
                    || this.placementData.code === 'TopPaintfill'
                )) {
                    return true;
                }
                return this.selections.some((x) => x.placementCode === 'Wedge');
            },

            price() {
                if (!this.shouldShowPrice) {
                    return null;
                }

                if (!this.currentComponent) {
                    return '─ ─';
                }

                return this.customizerStore.priceFormatted(
                    this.rawPriceForComponent(this.currentComponent),
                    true,
                );
            },

            formattedCategoryName() {
                switch (this.selections[0].category) {
                case 'Color Changes':
                    return this.$t('COLOR_CHANGES');
                case 'PutterShaftBand':
                    return this.$t('CATEGORY_SHAFT_BAND');
                case 'TopPaintfill':
                    return this.$t('PAINTFILL');
                case 'BackPaintfill':
                    return this.$t('PAINTFILL');
                default:
                    return this.$te(`CATEGORY_CODE_${this.selections[0].category}`)
                        ? this.$t(`CATEGORY_CODE_${this.selections[0].category}`)
                        : this.selections[0].category;
                }
            },
        },

        methods: {
            buildSelectionsForGroup(groupCode) {
                if (groupCode === 'Paintfill') {
                    const category = this.getCategoryFromPlacementCode('Paintfill');

                    if (!this.customizerStore.isPaintfillComplete('Face/Sole')) {
                        return [{
                            name: 'MAKE_SELECTION',
                            placementCode: 'PutterFrontPaintfill',
                            category,
                        }];
                    }

                    return [{
                        name: PAINTFILL_SUMMARY_LINK,
                        placementCode: 'PutterFrontPaintfill',
                        category,
                    }];
                }

                const group = this.customizerStore.visibleGroups.find((g) => g.code === groupCode);
                if (!group) {
                    return [];
                }

                const category = this.getCategoryFromPlacementCode(groupCode);
                const results = [];

                let hasMain = false;
                if (group.placements.length > 0) {
                    const firstPlacement = group.placements[0];
                    if (firstPlacement.custom?.['v-main-selection'] === 'yes') {
                        const firstSelection = this.viewController?.selectedAtPlacement(firstPlacement.code);
                        hasMain = true;
                        if (firstSelection?.placeholder) {
                            results.push({
                                name: 'MAKE_SELECTION',
                                placementCode: firstPlacement.code,
                                category,
                            });
                        }
                    }
                }

                let index = 0;
                group.placements.forEach((placement) => {
                    // Skip character stamping inline color placements; handled with text lines
                    if (placement.code === 'StampingColorsLine1' || placement.code === 'StampingColorsLine2' || placement.code === 'StampingColorsSymbols') {
                        return;
                    }

                    const selectedAtPlacement = this.viewController?.selectedAtPlacement(placement.code);
                    const selection = selectedAtPlacement?.placeholder ? null : selectedAtPlacement;

                    // Specific no-thanks handling
                    if (selectedAtPlacement?.code === 'Stamp-Stamp-NoThanks') {
                        results.push({
                            name: 'NO_TOE_ENGRAVING',
                            placementCode: placement.code,
                            category,
                        });
                        return;
                    }
                    if (selectedAtPlacement?.code === 'StampingStyle-StampingStyle-1') {
                        results.push({
                            name: 'NO_STAMPING',
                            placementCode: placement.code,
                            category,
                        });
                        return;
                    }

                    if (selection) {
                        if (placement.code === 'Stamp') {
                            if (selectedAtPlacement.code !== 'Stamp-Stamp-NoThanks') {
                                const stampName = this.getDescriptionText(selectedAtPlacement.description);
                                const stampColorPlacement = group.placements.find((x) => x.code === 'StampColor');
                                if (stampColorPlacement) {
                                    const colorSelection = this.viewController.selectedAtPlacement(stampColorPlacement.code);
                                    const colorName = (
                                        colorSelection && !colorSelection.placeholder
                                    )
                                        ? this.getDescriptionText(colorSelection.description)
                                        : null;
                                    results.push({
                                        name: stampName,
                                        placementCode: 'Stamp',
                                        category: this.$t('CATEGORY_STAMPS'),
                                    });
                                    if (colorName) {
                                        results.push({
                                            name: colorName,
                                            placementCode: 'StampColor',
                                            category: this.$t('CATEGORY_STAMPS'),
                                        });
                                    }
                                } else {
                                    results.push({
                                        name: stampName,
                                        placementCode: 'Stamp',
                                        category: this.$t('CATEGORY_STAMPS'),
                                    });
                                }
                            }
                        } else if (placement.code === 'StampingStyle') {
                            if (!selectedAtPlacement.placeholder || selectedAtPlacement.placeholderOptional) {
                                if (selectedAtPlacement.code !== 'StampingStyle-StampingStyle-1') {
                                    const custom = selectedAtPlacement.custom || {};
                                    const thumbnailAsset = custom['v-thumbnail-asset'] || '';

                                    let letterStyle = '';
                                    if (thumbnailAsset.toLowerCase().includes('dancing')) {
                                        letterStyle = 'Dancing';
                                    } else if (thumbnailAsset.toLowerCase().includes('straight')) {
                                        letterStyle = 'Straight';
                                    }

                                    const descriptionText = this.getDescriptionText(selectedAtPlacement.description);
                                    const hasDots = descriptionText.toLowerCase().includes('dots');

                                    let stampedName = 'Initials';
                                    if (letterStyle) {
                                        stampedName += `: ${letterStyle}`;
                                    }
                                    if (hasDots) {
                                        stampedName += ' | Separate With Periods';
                                    }

                                    results.push({
                                        name: stampedName,
                                        placementCode: placement.code,
                                        category: this.$t('CATEGORY_STAMPING'),
                                    });
                                }
                            }
                        } else if (placement.code === 'StampSymbols') {
                            // Symbols handled together with its color later
                        } else {
                            results.push({
                                name: this.getDescriptionText(selection.description),
                                placementCode: placement.code,
                                category,
                            });
                        }
                    } else if (hasMain) {
                        results.push({
                            name: index === 0 ? 'MAKE_SELECTION' : '',
                            placementCode: placement.code,
                            category,
                        });
                    } else {
                        results.push({
                            name: 'MAKE_SELECTION',
                            placementCode: placement.code,
                            category,
                        });
                        return;
                    }

                    // Character stamping custom text and colors
                    if (placement.code === 'StampingStyle') {
                        const chars = [];
                        const colors = [];
                        if (selection?.custom?.['v-stamping-text-1'] && selection.custom['v-stamping-text-1'].length > 0) {
                            chars.push(selection.custom['v-stamping-text-1']);
                            const c1 = group.placements.find((x) => x.code === 'StampingColorsLine1');
                            if (c1) {
                                const sel = this.viewController.selectedAtPlacement(c1.code);
                                if (sel && !sel.placeholder) {
                                    colors.push(this.getDescriptionText(sel.description));
                                }
                            }
                        }
                        if (selection?.custom?.['v-stamping-text-2'] && selection.custom['v-stamping-text-2'].length > 0) {
                            chars.push(selection.custom['v-stamping-text-2']);
                            const c2 = group.placements.find((x) => x.code === 'StampingColorsLine2');
                            if (c2) {
                                const sel = this.viewController.selectedAtPlacement(c2.code);
                                if (sel && !sel.placeholder) {
                                    colors.push(this.getDescriptionText(sel.description));
                                }
                            }
                        }
                        if (chars.length > 0) {
                            results.push({
                                name: chars.join(' / '),
                                placementCode: placement.code,
                                category: this.$t('CATEGORY_STAMPING'),
                            });
                            results.push({
                                name: colors.join(' / '),
                                placementCode: 'StampingColorsLines',
                                category: this.$t('CATEGORY_STAMPING'),
                            });
                        } else {
                            // If no text present, still show chosen colors
                            const c1 = group.placements.find((x) => x.code === 'StampingColorsLine1');
                            const c2 = group.placements.find((x) => x.code === 'StampingColorsLine2');
                            const colorsFallback = [];
                            if (c1) {
                                const sel1 = this.viewController.selectedAtPlacement(c1.code);
                                if (sel1 && !sel1.placeholder) {
                                    colorsFallback.push(this.getDescriptionText(sel1.description));
                                }
                            }
                            if (c2) {
                                const sel2 = this.viewController.selectedAtPlacement(c2.code);
                                if (sel2 && !sel2.placeholder) {
                                    colorsFallback.push(this.getDescriptionText(sel2.description));
                                }
                            }
                            if (colorsFallback.length > 0) {
                                results.push({
                                    name: colorsFallback.join(' / '),
                                    placementCode: 'StampingColorsLines',
                                    category: this.$t('CATEGORY_STAMPING'),
                                });
                            }
                        }
                    }

                    // Symbols and symbol color
                    if (placement.code === 'StampSymbols' && selection && !selection.placeholder) {
                        const symbol = group.placements.find((x) => x.code === 'StampSymbols');
                        const color = group.placements.find((x) => x.code === 'StampingColorsSymbols');
                        if (symbol && color) {
                            const symbolSelection = this.viewController.selectedAtPlacement(symbol.code);
                            const colorSelection = this.viewController.selectedAtPlacement(color.code);
                            if (
                                symbolSelection
                                && colorSelection
                                && !symbolSelection.placeholder
                                && !colorSelection.placeholder
                            ) {
                                results.push({
                                    name: this.getDescriptionText(colorSelection.description),
                                    placementCode: placement.code,
                                    category: this.$t('CATEGORY_STAMPING'),
                                });
                            }
                        }
                    }

                    index += 1;
                });

                // Dedup and filter blanks
                const filtered = results.filter((x) => x && x.name && x.name.length > 0);
                const deDupped = [];
                filtered.forEach((entry) => {
                    if (!deDupped.find((x) => x.name === entry.name && x.category === entry.category && x.placementCode === entry.placementCode)) {
                        deDupped.push(entry);
                    }
                });

                return deDupped;
            },
            shouldShowPaintfillSummaryLink(selections) {
                return selections.some((selection) => selection.name === PAINTFILL_SUMMARY_LINK);
            },
            getCategoryFromPlacementCode(code) {
                switch (code) {
                case 'Stamping':
                case 'CharacterStamping':
                    return this.$t('CATEGORY_INITIALS');
                case 'Stamps':
                    return this.$t('CATEGORY_STAMPS');
                case 'Paintfill':
                    return this.$t('PAINTFILL');
                case 'TopPaintfill':
                    return this.$t('TOP_PAINTFILL');
                case 'BackPaintfill':
                    return this.$t('BACK_PAINTFILL');
                case 'Specifications':
                    return this.$t('CATEGORY_LOFTLIE');
                default:
                    return code;
                }
            },
            getSpecificationCategoryFromPlacement(placement) {
                switch (placement.code) {
                case 'LoftAdjustment':
                case 'LieAdjustment':
                    return `${this.$t('CURRENT_LOFT')} & ${this.$t('CURRENT_LIE')}`;
                case 'WeightAdjustment':
                    return this.$t('CURRENT_WEIGHT');
                default:
                    return placement.name?.replace(/^Adjust\s+/, '') || placement.code;
                }
            },
            selectedComponentForSummaryGroup(groupCode) {
                const group = this.customizerStore.visibleGroups.find((visibleGroup) => visibleGroup.code === groupCode);
                const placement = group?.placements?.[0];

                if (!placement || placement.code === 'Wedge') {
                    return null;
                }

                const component = this.viewController.selectedAtPlacement(placement.code);

                if (!component || component.placeholder) {
                    return null;
                }

                return component;
            },
            rawPriceForComponent(component) {
                if (!component) {
                    return null;
                }

                return this.customizerStore.getPrice(this.viewController, component, 'basePrice');
            },
            getPaintfillLocationSelectionName(placement, selection) {
                const location = placement.name;
                const color = this.getDescriptionText(selection.description);

                if (!location) {
                    return color;
                }

                return `${location}: ${color}`;
            },
            getDescriptionText(description) {
                let text = '';

                description.forEach((desc) => {
                    text += ` ${desc.name}`;
                });

                return text.trim();
            },
            async redirectToPanel() {
                this.$emit('redirect-to-panel', this.placementData);
            },
        },
    };
</script>
